import { OrderState, OrderType } from '../types/orders';

/**
 * ¿Se abre el aviso «Todas las actuaciones validadas» (T99) para el padre de un
 * expediente? Lo decide el hook `useCloseOrderConfirmation` justo después de
 * leer el padre, antes de nada más.
 *
 * Spec: `docs/superpowers/specs/2026-10-12-aviso-cierre-recorrido-design.md` § 8.
 */

/** Lo que la decisión necesita saber del padre. */
export type ParentOrderSnapshot = {
  state?: string | null;
  type?: string | null;
};

/** Cómo fue la lectura del padre. */
export type ParentOrderLookup<T extends ParentOrderSnapshot = ParentOrderSnapshot> =
  // Leído de red o, si la red falló, de la caché.
  | { status: 'found'; order: T }
  // El servidor contestó que no existe.
  | { status: 'missing' }
  // Sin red y sin copia en caché: no se sabe qué es.
  | { status: 'unreachable' };

export type CloseOrderPromptDecision<T extends ParentOrderSnapshot = ParentOrderSnapshot> =
  | { action: 'open'; order: T }
  | {
      action: 'discard';
      reason: 'not-found' | 'already-closed' | 'route-not-delivered';
    }
  | { action: 'retry-later' };

export function decideCloseOrderPrompt<T extends ParentOrderSnapshot>(
  lookup: ParentOrderLookup<T>,
): CloseOrderPromptDecision<T> {
  // Sin saber qué es, no se abre. Abierto «a ciegas» sale como OT genérica, y
  // en un recorrido eso ofrecería CLOSE_ORDER sobre su orden gemela, que el
  // servidor acepta desde cualquier estado (spec § 9.3). El pendiente se queda
  // y se reintenta en la siguiente pantalla que monte el aviso.
  if (lookup.status === 'unreachable') return { action: 'retry-later' };
  if (lookup.status === 'missing') {
    return { action: 'discard', reason: 'not-found' };
  }

  const { order } = lookup;
  // 🔒 Corte de bucle (prod): si la OT ya está cerrada o anulada no hay nada
  // que cerrar. Sin esto, un cierre que llegó al servidor durante un deploy,
  // pero cuya respuesta se perdió, reabría el aviso para siempre: el reintento
  // chocaba con «Cannot close an order that is already closed».
  if (order.state === OrderState.End || order.state === OrderState.Annulled) {
    return { action: 'discard', reason: 'already-closed' };
  }
  // En un recorrido el aviso ofrece VALIDARLO, y el servidor solo lo permite
  // desde «Pendiente de validar» (SR_VALIDATE_ONLY_PENDING). Las básicas sí se
  // validan con el recorrido en marcha: sin esto el aviso preguntaba algo
  // imposible y volvía a salir en cada pantalla hasta pulsar «No».
  if (
    order.type === OrderType.SurveillanceRoute &&
    order.state !== OrderState.PendingValidation
  ) {
    return { action: 'discard', reason: 'route-not-delivered' };
  }
  return { action: 'open', order };
}

/** Lo que contestó `actionExpeditionCloseInfo` justo antes de abrir el aviso. */
export type ExpeditionCloseCheck =
  | {
      status: 'answered';
      allActionsValidated?: boolean | null;
      canCloseActions?: boolean | null;
    }
  // La consulta falló (sin red, servidor caído).
  | { status: 'unreachable' }
  // El padre no tiene expediente: no hay nada que preguntar.
  | { status: 'not-applicable' };

export type ExpeditionCloseDecision =
  | { action: 'open'; canCloseActions: boolean }
  | { action: 'discard'; reason: 'no-longer-complete' }
  | { action: 'retry-later' };

/**
 * Comprobación de última hora: la nota se apuntó cuando todo estaba terminado,
 * pero entre eso y abrir el aviso puede haber pasado de todo: otro usuario
 * reabre o crea una actuación, la cola sin red sube un cambio al volver la
 * conexión… Si ya no está todo terminado, la nota sobra. Antes el aviso se
 * abría igual y «Sí» fallaba en el servidor. Sin red para comprobarlo, no se
 * abre y se reintenta (spec 2026-10-12-aviso-cierre-recorrido § 12).
 */
export function decideFromExpeditionCheck(
  check: ExpeditionCloseCheck,
): ExpeditionCloseDecision {
  if (check.status === 'unreachable') return { action: 'retry-later' };
  if (check.status === 'not-applicable') {
    return { action: 'open', canCloseActions: false };
  }
  if (check.allActionsValidated !== true) {
    return { action: 'discard', reason: 'no-longer-complete' };
  }
  // Un botón de menos es una molestia; uno de más cierra trabajo que alguien
  // quería facturar: solo con `true` explícito.
  return { action: 'open', canCloseActions: check.canCloseActions === true };
}

/** Estados de una actuación que, al cambiarlos con el chip, pueden dejarlo todo terminado. */
const STATE_CHANGE_CLOSE_TRIGGERS: readonly string[] = [
  OrderState.Validated,
  OrderState.PendingCertification,
  OrderState.End,
];

/** ¿Tras pasar una actuación a este estado hay que preguntar si ya se puede cerrar la OT? */
export function isStateChangeCloseTrigger(newState: string | null | undefined): boolean {
  return !!newState && STATE_CHANGE_CLOSE_TRIGGERS.includes(newState);
}

/** Lo que contestó `actionExpeditionCloseInfo` tras cambiar el estado de una actuación. */
export type StateChangeCloseCheck = {
  allActionsValidated?: boolean | null;
  canCloseActions?: boolean | null;
  parentOrderId?: string | null;
  parentRoid?: string | null;
};

export type StateChangeNoteDecision =
  | { action: 'skip' }
  | {
      action: 'note';
      parentOrderId: string;
      parentRoid?: string;
      // Borrar antes el «No, mantener abierta» de esta OT: si no, el reducer
      // descarta la nota y el aviso no sale.
      clearDecline: boolean;
    };

/**
 * Tras cambiar con el chip el estado de una actuación: ¿se apunta la nota del
 * aviso de cierre y se olvida un «No» anterior?
 *
 * A Finalizada solo se pregunta si ya cabe «Cerrar la OT y sus actuaciones»
 * (`canCloseActions`): cerrar de una en una las pendientes de certificar no
 * puede abrir el aviso en cada cierre intermedio sin nada nuevo que ofrecer.
 *
 * El «No» se olvida salvo al cerrar a mano una actuación que ya estaba
 * validada. Ahí el botón ya existía y quien dijo que no no quiere que se le
 * pregunte en cada una. Cualquier otro origen es trabajo que bloqueaba el
 * cierre y acaba de terminarse: en el caso de la última pendiente de
 * certificar, el botón aparece justo ahora y el «No» se dio a un aviso que no
 * lo tenía. Sin saber de dónde venía, no se insiste. Spec
 * `docs/superpowers/specs/2026-10-28-aviso-cierre-tras-certificar-design.md` § 4.
 */
export function decideCloseNoteAfterStateChange(
  previousState: string | null | undefined,
  newState: string | null | undefined,
  check: StateChangeCloseCheck | null | undefined,
): StateChangeNoteDecision {
  if (!isStateChangeCloseTrigger(newState)) return { action: 'skip' };
  if (!check?.allActionsValidated || !check.parentOrderId) return { action: 'skip' };

  const isEnd = newState === OrderState.End;
  if (isEnd && check.canCloseActions !== true) return { action: 'skip' };

  const clearDecline =
    !isEnd || (!!previousState && previousState !== OrderState.Validated);
  return {
    action: 'note',
    parentOrderId: check.parentOrderId,
    parentRoid: check.parentRoid ?? undefined,
    clearDecline,
  };
}
